import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useApp } from '@/components/ds';
import { Spinner } from '@/components/code';

/**
 * Auth + onboarding gate for the signed-in areas of the portal.
 *
 *   mode="app"     → requires a signed-in, onboarded user (the dashboard). Un-onboarded users are
 *                    sent to /onboarding.
 *   mode="authed"  → requires only a signed-in user (the onboarding flow). The "already onboarded,
 *                    go to the dashboard" bounce is handled once, on mount, inside the onboarding
 *                    container — not reactively here — so that marking the user onboarded mid-flow
 *                    (to unlock the dashboard) doesn't yank them off the final key-reveal step.
 *
 * Both wait for the initial `/session` load before deciding. If that bootstrap `/me` call failed
 * (`bootError`), we show an error page with a retry rather than the app — the melio-api DB is always
 * configured, so a failure is a real error, not an "undeterminable" state to fall through on.
 */
export function ProtectedRoute({ children, mode }: { children: ReactNode; mode: 'app' | 'authed' }) {
  const app = useApp();
  const { pathname, search, hash } = useLocation();

  if (app.authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={20} />
      </div>
    );
  }

  // Carry where we were, the same way AppProvider's `goSignIn` does for a session that expires
  // mid-visit: /signin hands `returnTo` to the backend, which round-trips it through the httpOnly
  // OAuth transaction cookie. Without it every guarded page came back to the `/dashboard` default.
  if (!app.authed) {
    return <Navigate to={`/signin?returnTo=${encodeURIComponent(`${pathname}${search}${hash}`)}`} replace />;
  }

  if (app.bootError) return <BootErrorPage />;

  if (mode === 'app' && app.onboarded === false) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

/** Shown when the bootstrap `/me` call failed — a full reload re-runs the one-time session load. */
function BootErrorPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <h1 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.01em' }}>We couldn't load your account</h1>
      <p style={{ margin: 0, fontSize: 15, color: 'var(--text-secondary)', maxWidth: 420 }}>
        Something went wrong reaching the server. This is usually temporary — please try again.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: 6,
          height: 40,
          padding: '0 20px',
          borderRadius: 8,
          border: 'none',
          background: 'var(--accent)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
}
